import React, { ReactNode, createContext, useContext, useState, useCallback } from "react";
import { Report } from "../_models/Report.model";

interface ReportContextProps {
  newReport: Report;
  setNewReport: React.Dispatch<React.SetStateAction<Report>>;
  currentViewingReport: Report;
  setCurrentViewingReport: React.Dispatch<React.SetStateAction<Report>>;
  filteredInternalReportsForSelectedClient: Report[];
  setFilteredInternalReportsForSelectedClient: React.Dispatch<
    React.SetStateAction<Report[]>
  >;
  filteredInspectionReportsForSelectedClient: Report[];
  setFilteredInspectionReportsForSelectedClient: React.Dispatch<
    React.SetStateAction<Report[]>
  >;
  printableReportPageHeight: number;
  setPrintableReportPageHeight: React.Dispatch<React.SetStateAction<number>>;
  updateReportedDate: () => void;
  setManualReportedDate: (date: string) => void;
  areaBasedScoringAreabuttonsClicked: { [key: string]: string | null };
  setAreaBasedScoringAreabuttonsClicked: React.Dispatch<React.SetStateAction<{ [key: string]: string | null }>>;
  isNewReport: boolean;
  setIsNewReport: React.Dispatch<React.SetStateAction<boolean>>;
  fetchedAllReports: Report[]
  setFetchedAllReports: React.Dispatch<
    React.SetStateAction<Report[]>
  >;
  viewedAreas: Set<string>;
  setViewedAreas: React.Dispatch<React.SetStateAction<Set<string>>>;
}




const ReportContext = createContext<ReportContextProps | null>(null);

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error(
      "useReportContext must be used within a report context provider"
    );
  }
  return context;
};

interface ReportContextProviderProps {
  children: ReactNode;
}

export const ReportContextProvider: React.FC<ReportContextProviderProps> = ({
  children,
}) => {
  const [currentViewingReport, setCurrentViewingReport] = useState<Report>(
    () => {
      const savedReport = localStorage.getItem("viewingReport");
      return savedReport ? JSON.parse(savedReport) : ({} as Report);
    }
  );

  const [areaBasedScoringAreabuttonsClicked, setAreaBasedScoringAreabuttonsClicked] = useState<{
    [key: string]: string | null;
  }>(() => {
    const savedState = localStorage.getItem("buttonClicked");
    return savedState ? JSON.parse(savedState) : {};
  });

  const [newReport, setNewReport] = useState<Report>(() => {
    const savedReport = localStorage.getItem("newReport");
    return savedReport ? JSON.parse(savedReport) : ({} as Report);
  });

  const [viewedAreas, setViewedAreas] = useState<Set<string>>(new Set());
  const [isNewReport, setIsNewReport] = useState<boolean>(true)



  const [
    filteredInternalReportsForSelectedClient,
    setFilteredInternalReportsForSelectedClient,
  ] = useState<Report[]>([]);
  const [
    filteredInspectionReportsForSelectedClient,
    setFilteredInspectionReportsForSelectedClient,
  ] = useState<Report[]>([]);

  const [fetchedAllReports, setFetchedAllReports] = useState<Report[]>([]);

  const [printableReportPageHeight, setPrintableReportPageHeight] =
    useState<number>(0);

  const handleSetViewingReport: React.Dispatch<React.SetStateAction<Report>> = useCallback(
    (value) => {
      setCurrentViewingReport((prevReport) => {
        const newViewReport =
          typeof value === "function" ? value(prevReport) : value;

        try {
          localStorage.setItem("viewingReport", JSON.stringify(newViewReport));
        } catch (e) {
          console.error("Failed to save viewingReport to localStorage", e);
        }

        return newViewReport;
      });
    },
    []
  );

  const handleSetNewReport: React.Dispatch<React.SetStateAction<Report>> = useCallback(
    (value) => {
      setNewReport((prevReport) => {
        const newCreatingReport =
          typeof value === "function" ? value(prevReport) : value;

        try {
          localStorage.setItem("newReport", JSON.stringify(newCreatingReport));
        } catch (e) {
          console.error("Failed to save newReport to localStorage", e);
        }

        return newCreatingReport;
      });
    },
    []
  );

  const updateReportedDate = useCallback(() => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    handleSetNewReport((prevReport) => ({
      ...prevReport,
      ReportedDate: formattedDate,
    }));
  }, [handleSetNewReport]);

  const setManualReportedDate = useCallback(
    (date: string) => {
      handleSetNewReport((prevReport) => ({
        ...prevReport,
        ReportedDate: date,
      }));
    },
    [handleSetNewReport]
  );




  const handleSetAreaBasedScoring_AreaButtonsClicked = (value: React.SetStateAction<{ [key: string]: string | null }>) => {
    const newState =
      typeof value === "function" ? value(areaBasedScoringAreabuttonsClicked) : value;

    // Save the new state to localStorage
    localStorage.setItem("buttonClicked", JSON.stringify(newState));

    // Update the state
    setAreaBasedScoringAreabuttonsClicked(newState);
  };

  return (
    <ReportContext.Provider
      value={{
        currentViewingReport,
        setCurrentViewingReport: handleSetViewingReport,
        filteredInternalReportsForSelectedClient,
        setFilteredInternalReportsForSelectedClient,
        printableReportPageHeight,
        setPrintableReportPageHeight,
        filteredInspectionReportsForSelectedClient,
        setFilteredInspectionReportsForSelectedClient,
        newReport,
        setNewReport: handleSetNewReport,
        updateReportedDate,
        setManualReportedDate,
        areaBasedScoringAreabuttonsClicked,
        setAreaBasedScoringAreabuttonsClicked: handleSetAreaBasedScoring_AreaButtonsClicked,
        isNewReport,
        setIsNewReport,
        fetchedAllReports, setFetchedAllReports,
        viewedAreas,
        setViewedAreas
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};
